import Button from '@rio-cloud/rio-uikit/Button';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import type { MenuItemProps as MenuItem } from '@rio-cloud/rio-uikit/components/menuItems/MenuItem';
import { useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { useProductInMaintenanceMode } from '../../../../../configuration/featureToggle/hooks/useProductInMaintenanceMode';
import type { RootState } from '../../../../../configuration/setup/store';
import { isPresent, type UikitOnSelectFn } from '../../../../../utils/typescriptUtil';
import { ProductType } from '../../../common/product/product';
import { isRatePlanBookable } from '../../../common/product/productService';
import { getProductLabel } from '../../../common/utils/productLabelUtil';
import type { OverviewResource } from '../redux/types';
import { getServiceOverviewAutoTrackingKey } from '../serviceOverviewTracking';
import { openCheckoutPopup } from '../services/checkoutService';
import { GtmPromotion } from '../services/gtmPromotion';
import { isSubscriptionCancelling } from '../services/subscriptionService';
import type { ServiceOverviewItem } from '../types';
import { CancelResourceSubscriptionsDialog } from './CancelResourceSubscriptionsDialog';

interface Props {
    service: ServiceOverviewItem;
    currentLevel?: string;
    isCurrentlyProcessingCancellation: (sku: string) => boolean;
    handleConfirmUpdate: () => void;
    selectedResources: OverviewResource[];
    wouldCancelService: boolean;
    serviceName: string;
}

const actionItemClassName = 'white-space-nowrap';

function getUpgradeDowngradeLabel(isUpgrade: boolean, intl: IntlShape, levelName: string) {
    return (
        <div className={actionItemClassName}>
            <span className={`margin-right-5 uikit-icon icon-arrow-${isUpgrade ? 'up' : 'down'}`} aria-hidden='true' />
            <span>{`${
                isUpgrade
                    ? `${intl.formatMessage({ id: 'intl-msg:marketplace.services.upgrade' })}:`
                    : `${intl.formatMessage({ id: 'intl-msg:marketplace.services.downgrade' })}:`
            } ${levelName}`}</span>
        </div>
    );
}

function CancelLabel() {
    return (
        <div className={actionItemClassName}>
            <span className='margin-right-5 uikit-icon icon-trash text-color-danger' aria-hidden='true' />
            <span>
                <FormattedMessage id={'intl-msg:marketplace.myServices.subscription.cancel.button'} />
            </span>
        </div>
    );
}

export function SelectionActions(props: Props) {
    const intl = useIntl();
    const {
        service,
        currentLevel,
        isCurrentlyProcessingCancellation,
        handleConfirmUpdate,
        selectedResources,
        wouldCancelService,
        serviceName,
    } = props;
    const [showDialog, setShowDialog] = useState(false);
    const isProductInMaintenance = useProductInMaintenanceMode(service.sku);

    if (!service.hasRatePlan) {
        return null;
    }

    const openUpDowngradePopup = (promotion: GtmPromotion, level?: string) => {
        const { sku, hasRatePlan, legacy, productRatePlans } = service;
        if (!hasRatePlan || legacy) {
            return () => {
                throw new Error();
            };
        }
        const levelExtra = productRatePlans && productRatePlans.length > 1 ? level : undefined;
        const openCheckout = openCheckoutPopup(
            sku,
            promotion,
            levelExtra,
            selectedResources.map(it => it.id)
        ).callback;

        return openCheckout as unknown as UikitOnSelectFn;
    };
    const upgradeDowngradeActions = (service.productRatePlans ?? [])
        .filter(ratePlan => ratePlan.level !== currentLevel)
        .filter(isRatePlanBookable)
        .filter(ratePlan => isPresent(ratePlan))
        .map(ratePlan => {
            const isUpgrade = (ratePlan.level ?? '0') > (currentLevel ?? '0');
            const action: MenuItem = {
                value: getUpgradeDowngradeLabel(isUpgrade, intl, getProductLabel(ratePlan, intl)),
                onSelect: isUpgrade
                    ? openUpDowngradePopup(GtmPromotion.customerCenterUpgrade, ratePlan.level)
                    : openUpDowngradePopup(GtmPromotion.customerCenterDowngrade, ratePlan.level),
            };
            return action;
        });
    const cancelAction: MenuItem = {
        value: <CancelLabel />,
        onSelect: () => setShowDialog(true),
    };
    const actionItems: MenuItem[] = [...upgradeDowngradeActions, cancelAction];

    if (isCurrentlyProcessingCancellation(service.sku) || selectedResources.length === 0) {
        const noSelectionTranslationKey =
            service.productType === ProductType.USER_BASED
                ? 'intl-msg:marketplace.services.users.noSelection.dropdown'
                : 'intl-msg:marketplace.services.noSelection.dropdown';
        const selectionButtonLabel = intl.formatMessage({ id: noSelectionTranslationKey });

        return actionItems.length === 1 ? (
            <span title={selectionButtonLabel}>
                <Button
                    className={'btn btn-danger btn-outline'}
                    disabled
                    autoTrackingKey={getServiceOverviewAutoTrackingKey(
                        'marketplace.myServices.subscription.cancel.button',
                        service.productName ?? service.sku
                    )}
                >
                    <CancelLabel />
                </Button>
            </span>
        ) : (
            <ButtonDropdown title={selectionButtonLabel} items={actionItems} disabled />
        );
    }
    const useSelectionFor = intl.formatMessage({ id: 'intl-msg:marketplace.services.selection.dropdown' });

    return (
        <div>
            {actionItems.length === 1 ? (
                <Button
                    className={'btn btn-danger btn-outline'}
                    onClick={() => setShowDialog(true)}
                    autoTrackingKey={getServiceOverviewAutoTrackingKey(
                        'marketplace.myServices.subscription.cancel.button',
                        service.productName ?? service.sku
                    )}
                >
                    <span className={'margin-right-5'}>({selectedResources.length})</span>
                    <CancelLabel />
                </Button>
            ) : (
                <ButtonDropdown
                    title={`(${selectedResources.length}) ${useSelectionFor}`}
                    items={actionItems}
                    bsStyle='primary'
                    dropdownClassName='width-auto'
                />
            )}
            <CancelResourceSubscriptionsDialog
                show={showDialog}
                isMaintenanceModeEnabled={isProductInMaintenance}
                serviceName={serviceName}
                selectedResources={selectedResources}
                wouldCancelService={wouldCancelService}
                onClickCancelCallback={() => setShowDialog(false)}
                onClickConfirmCallback={() => {
                    setShowDialog(false);
                    handleConfirmUpdate();
                }}
                productType={service.productType}
            />
        </div>
    );
}

const mapStateToProps = (state: RootState) => ({
    isCurrentlyProcessingCancellation: (sku: string, resourceId?: string) =>
        isSubscriptionCancelling(state, sku, resourceId),
});

export const SelectionActionsContainer = connect(mapStateToProps)(SelectionActions);
